'use client';

import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/cn';

export type MarqueeDirection = 'rtl' | 'ltr';

export interface MarqueeProps {
    children: ReactNode;
    className?: string;
    direction?: MarqueeDirection;
    speed?: number;
}

export default function Marquee({ children, className, direction = 'rtl', speed = 120 }: MarqueeProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [repeatCount, setRepeatCount] = useState(2);
    const [duration, setDuration] = useState(4);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const content = contentRef.current;
        if (!container || !content) {
            return;
        }

        const contentWidth = content.offsetWidth;
        if (contentWidth === 0) {
            return;
        }

        const calculateRepeatCount = () => {
            const containerWidth = container.offsetWidth;
            const needed = Math.ceil(containerWidth / contentWidth) + 1;
            setRepeatCount(needed);
            setDuration((contentWidth / 100) * (speed / 60));
        };

        calculateRepeatCount();

        const observer = new ResizeObserver(calculateRepeatCount);
        observer.observe(container);

        return () => observer.disconnect();
    }, [children, speed]);

    const start = direction === 'rtl' ? '0%' : '-100%';
    const end = direction === 'rtl' ? '-100%' : '0%';

    const repeatedContent = Array.from({ length: repeatCount }).map((_, i) => (
        <motion.div
            animate={{ x: [start, end] }}
            aria-hidden={i > 0}
            className="flex-shrink-0 nowrap"
            key={`${repeatCount}-${i}`}
            ref={i === 0 ? contentRef : undefined}
            transition={{
                x: { repeat: Infinity, duration, ease: 'linear' },
            }}
        >
            {children}
        </motion.div>
    ));

    return (
        <div ref={containerRef} className={cn('overflow-hidden', className)}>
            <div className="flex">{repeatedContent}</div>
        </div>
    );
}
